import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import MuiSelect from '@mui/material/Select';
import MuiFormControl from '@mui/material/FormControl';
import MuiInputLabel from '@mui/material/InputLabel';
import withController from '../lib/withController';

const InputLabel = ({ ...properties }) => <MuiInputLabel {...properties} />;

const selectPropertyTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.number.isRequired,
  ]),
  inputProps: PropTypes.shape({
    label: PropTypes.string,
  }),
  onChange: PropTypes.func,
  notched: PropTypes.bool,
  children: PropTypes.node,
};

type SelectProperties = InferProps<
typeof selectPropertyTypes
>;

const Select = React.forwardRef(({
  label,
  value,
  inputProps,
  notched,
  id,
  onChange,
  ...properties
}: SelectProperties, reference) => (
  <MuiSelect
    native
    label={label}
    inputProps={{
      ...inputProps,
      label,
      'aria-label': label ?? undefined,
    }}
    notched={notched ?? undefined}
    id={id ?? undefined}
    value={value}
    onChange={onChange ?? undefined}
    ref={reference}
    {...properties}
  />
));

Select.propTypes = selectPropertyTypes;

const optionPropertyTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
};

type OptionProperties = InferProps<
typeof optionPropertyTypes
>;

const Option = (
  { ...properties }: OptionProperties,
) => <option {...properties} />;

const optionsPropertyTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
};

type OptionsProperties = InferProps<
typeof optionsPropertyTypes
>;

const Options = ({ options }: OptionsProperties) => (
  <>
    {options.map(
      ({ value, label }) => (
        <Option
          value={value}
          key={value}
        >
          {label}
        </Option>
      ),
    )}
  </>
);

Options.propTypes = optionsPropertyTypes;

const FormControl = ({ ...properties }) => <MuiFormControl {...properties} />;

const selectWithControllerPropertyTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ).isRequired,
  variant: PropTypes.string.isRequired,
  fullWidth: PropTypes.bool.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  rules: PropTypes.shape({}).isRequired,
  shrinkLabel: PropTypes.bool.isRequired,
  value: PropTypes.string,
};

type SelectWithControllerProperties = InferProps<
typeof selectWithControllerPropertyTypes
>;

const SelectWithController = withController(
  React.forwardRef(({
    variant,
    fullWidth,
    rules,
    id,
    shrinkLabel,
    label,
    value,
    options,
    ...properties
  }: SelectWithControllerProperties, reference) => (
    <FormControl
      variant={variant}
      fullWidth={fullWidth}
      rules={rules}
    >
      <InputLabel
        htmlFor={id}
        shrink={shrinkLabel}
      >
        {label}
      </InputLabel>
      <Select
        notched={shrinkLabel}
        id={id}
        label={label}
        value={value ?? ''}
        ref={reference}
        {...properties}
      >
        <Options options={options} />
      </Select>
    </FormControl>
  )),
);

export {
  SelectWithController, Select, Option, Options, FormControl, InputLabel,
};
